import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import CookieBanner from "@/components/CookieBanner";

import Home from "@/pages/Home";
import Product from "@/pages/Product";
import HowItWorks from "@/pages/HowItWorks";
import Templates from "@/pages/Templates";
import Solutions from "@/pages/Solutions";
import Security from "@/pages/Security";
import Pricing from "@/pages/Pricing";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import VerifyPending from "@/pages/VerifyPending";
import AcceptInvite from "@/pages/AcceptInvite";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/app/Dashboard";
import NewDocument from "@/pages/app/NewDocument";
import DocumentsPage from "@/pages/app/Documents";
import DocumentDetail from "@/pages/app/DocumentDetail";
import TemplatesPage from "@/pages/app/Templates";
import TemplateDetail from "@/pages/app/TemplateDetail";
import Standards from "@/pages/app/Standards";
import Uploads from "@/pages/app/Uploads";
import Team from "@/pages/app/Team";
import ActivityPage from "@/pages/app/Activity";
import Settings from "@/pages/app/Settings";
import Billing from "@/pages/app/Billing";

function Private({ children }) {
  const { user, loading, workspaces } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  // New unverified users must verify before setting up a workspace
  if (!user.email_verified && workspaces.length === 0 && location.pathname !== "/verify-pending") {
    return <Navigate to="/verify-pending" replace />;
  }
  if (workspaces.length === 0 && !["/onboarding", "/verify-pending"].includes(location.pathname)) return <Navigate to="/onboarding" replace />;
  return children;
}

function Guest({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="bottom-right" />
        <CookieBanner />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

function AppRoutes() {
  // read hash from useLocation, not window.location.hash (not reactive)
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/product" element={<Product />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/solutions/:industry" element={<Solutions />} />
          <Route path="/security" element={<Security />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Legal kind="privacy" />} />
          <Route path="/terms" element={<Legal kind="terms" />} />
          <Route path="/login" element={<Guest><Login /></Guest>} />
          <Route path="/signup" element={<Guest><Signup /></Guest>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-pending" element={<Private><VerifyPending /></Private>} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/onboarding" element={<Private><Onboarding /></Private>} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/app" element={<Private><AppShell /></Private>}>
            <Route index element={<Dashboard />} />
            <Route path="new" element={<NewDocument />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="documents/:id" element={<DocumentDetail />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="templates/:id" element={<TemplateDetail />} />
            <Route path="standards" element={<Standards />} />
            <Route path="uploads" element={<Uploads />} />
            <Route path="team" element={<Team />} />
            <Route path="activity" element={<ActivityPage />} />
            <Route path="settings" element={<Settings />} />
            <Route path="billing" element={<Billing />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}
